import styled from 'styled-components'

export const PreviewShell = styled.div`
  width: 100%;
  max-width: ${({ $standalone }) => ($standalone ? '100%' : '640px')};
  height: ${({ $standalone }) => ($standalone ? '100%' : 'auto')};
  max-height: ${({ $standalone }) => ($standalone ? '100%' : 'none')};
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark' ? theme.colors.backgroundSecondary : '#f1f5f9'};
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.1);
  overflow: hidden;
  display: ${({ $standalone }) => ($standalone ? 'flex' : 'block')};
  flex-direction: ${({ $standalone }) => ($standalone ? 'column' : 'initial')};
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};

  @media (min-width: 768px) {
    border-radius: ${({ $standalone }) =>
      $standalone ? '16px' : '16px 0 0 16px'};
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.12);
  }
`

export const PreviewChrome = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.4rem;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  background: ${({ theme }) => theme.colors.borderSubtle};

  @media (min-width: 768px) {
    padding: 0.75rem 1rem;
  }
`

export const PreviewDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.borderStrong};
`

export const PreviewBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  min-height: 0;
  flex: 1 1 auto;

  @media (min-width: 768px) {
    grid-template-columns: 178px 1fr;
    min-height: ${({ $standalone }) => ($standalone ? '0' : '320px')};
  }
`

export const PreviewSidebar = styled.aside`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0;
    border-right: 1px solid ${({ theme }) => theme.colors.borderDefault};
    background: ${({ theme }) => theme.colors.background};
  }
`

export const PreviewBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  height: 42px;
  padding: 0 0.7rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  flex-shrink: 0;
`

export const PreviewBrandName = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const PreviewNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0 0.55rem;
`

export const PreviewNavLabel = styled.span`
  display: block;
  margin: 0 0.35rem 0.15rem;
  font-size: 0.5625rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const PreviewNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

export const PreviewSidebarItem = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.4rem 0.5rem 0.4rem 0.55rem;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  letter-spacing: -0.01em;
  text-align: left;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  background: ${({ $active, theme }) =>
    $active
      ? theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.14)'
        : 'rgba(62, 207, 142, 0.1)'
      : 'transparent'};
  cursor: ${({ as }) => (as === 'span' ? 'default' : 'pointer')};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: ${({ $active }) => ($active ? '0.9rem' : '0')};
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.action.primary.bg};
  }
`

export const PreviewMenuIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 5px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.2)'
        : 'rgba(62, 207, 142, 0.12)'
      : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.bg : theme.colors.textMuted};

  span {
    font-size: 0.75rem !important;
    line-height: 1;
  }
`

export const PreviewHelpSection = styled.div`
  margin-top: auto;
  padding: 0.55rem 0.55rem 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

export const PreviewMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem 1rem 1.15rem;
  min-width: 0;

  @media (min-width: 768px) {
    gap: 0.75rem;
    padding: 1.15rem 1.25rem 1.35rem;
  }
`

export const PreviewPageTitle = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`

export const PreviewPageDesc = styled.p`
  margin: 0;
  font-size: 0.6875rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textMuted};

  @media (min-width: 768px) {
    font-size: 0.75rem;
    max-width: 28rem;
  }
`

export const PreviewHeroCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.15rem;
  padding: 1rem 1rem 0.9rem 1.15rem;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? `
        radial-gradient(ellipse 120% 90% at 0% 0%, rgba(62, 207, 142, 0.16), transparent 55%),
        ${theme.colors.background}
      `
      : `
        radial-gradient(ellipse 120% 90% at 0% 0%, rgba(62, 207, 142, 0.14), transparent 55%),
        ${theme.colors.background}
      `};
  box-shadow: 0 8px 20px rgba(33, 38, 45, 0.05);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: ${({ theme }) => theme.colors.action.primary.bg};
  }

  @media (min-width: 768px) {
    padding: 1.15rem 1.15rem 1rem 1.35rem;
    border-radius: 16px;

    &::before {
      width: 4px;
    }
  }
`

export const PreviewHeroTitle = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (min-width: 768px) {
    font-size: 0.9375rem;
  }
`

export const PreviewAmount = styled.div`
  margin: 0.1rem 0;
  font-size: 1.65rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;

  @media (min-width: 768px) {
    font-size: 2.15rem;
  }
`

export const PreviewSupport = styled.p`
  margin: 0;
  font-size: 0.6875rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`

export const PreviewMetaRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  margin-top: 0.4rem;

  @media (min-width: 520px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 0.85rem;
  }
`

export const PreviewPrevious = styled.span`
  font-size: 0.6875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`

export const PreviewTrend = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.action.primary.bg};

  span.las {
    font-size: 0.8rem;
    line-height: 1;
  }

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`

export const PreviewFootnote = styled.p`
  margin: 0.55rem 0 0;
  padding-top: 0.65rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.625rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textMuted};

  @media (min-width: 768px) {
    font-size: 0.6875rem;
  }
`

export const PreviewSectionLabel = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const PreviewChartCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.75rem 0.85rem 0.7rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
`

export const PreviewChartSubtitle = styled.span`
  font-size: 0.625rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textMuted};

  @media (min-width: 768px) {
    font-size: 0.6875rem;
  }
`

export const PreviewAreaChart = styled.svg`
  display: block;
  width: 100%;
  height: 56px;
  color: ${({ theme }) => theme.colors.borderStrong};

  .grid {
    opacity: 0.55;
  }

  .chart-line {
    stroke-dasharray: 420;
    stroke-dashoffset: 420;
    animation: drawLine 1.1s 0.15s ease-out forwards;
  }

  .chart-area {
    opacity: 0;
    animation: fadeArea 0.7s 0.45s ease-out forwards;
  }

  .chart-dot {
    opacity: 0;
    animation: fadeArea 0.35s 0.85s ease-out forwards;
  }

  @keyframes drawLine {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes fadeArea {
    to {
      opacity: 1;
    }
  }

  @media (min-width: 768px) {
    height: 68px;
  }
`

export const PreviewMethodToggle = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 3px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  gap: 2px;
`

export const PreviewMethodButton = styled.button`
  appearance: none;
  border: none;
  margin: 0;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.text : theme.colors.textMuted};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.bg : 'transparent'};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${({ $active, theme }) =>
      $active ? theme.colors.action.primary.text : theme.colors.textPrimary};
  }
`

export const PreviewLiveChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  align-self: flex-start;
  margin-bottom: 0.15rem;
  padding: 0.2rem 0.55rem 0.2rem 0.4rem;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.action.primary.bgActive};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(62, 207, 142, 0.14)'
      : 'rgba(62, 207, 142, 0.12)'};

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.action.primary.bg};
    box-shadow: 0 0 0 0 rgba(62, 207, 142, 0.55);
    animation: pulseDot 1.6s ease-out infinite;
  }

  @keyframes pulseDot {
    0% {
      box-shadow: 0 0 0 0 rgba(62, 207, 142, 0.5);
    }
    70% {
      box-shadow: 0 0 0 8px rgba(62, 207, 142, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(62, 207, 142, 0);
    }
  }
`

export const PreviewHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.35rem;
`

export const PreviewHistoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.8rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};

  strong {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  span {
    font-size: 0.6875rem;
    color: ${({ theme }) => theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`

export const PreviewHistoryStatus = styled.em`
  flex-shrink: 0;
  font-style: normal;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  color: ${({ $status, theme }) =>
    $status === 'pagado'
      ? theme.colors.action.primary.bgActive
      : theme.colors.textSecondary};
  background: ${({ $status, theme }) =>
    $status === 'pagado'
      ? theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.16)'
        : 'rgba(62, 207, 142, 0.12)'
      : theme.name === 'dark'
        ? 'rgba(148, 163, 184, 0.18)'
        : 'rgba(148, 163, 184, 0.2)'};
`

export const PreviewConfigCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 0.35rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  overflow: hidden;
`

export const PreviewConfigRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 0.95rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};

  &:last-child {
    border-bottom: none;
  }
`

export const PreviewConfigLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const PreviewConfigValue = styled.strong`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const PreviewSceneFade = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  animation: sceneIn 0.35s ease-out both;

  @keyframes sceneIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (min-width: 768px) {
    gap: 0.75rem;
  }
`
